import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import AsyncSelect from 'react-select/async';
import Modal from "react-bootstrap/Modal";
import {
  fetchManageList, createChart, deleteChart, reorderCharts,
} from "../../../store/actions/orgChart/orgChartActions";
import { formatDate, errorText } from "../../../components/Dashboard/OrgChart/orgChartModel";

const STATUS = {
  published: { label: "Published", cls: "badge oc-badge-ok" },
  draft_changes: { label: "Draft changes", cls: "badge badge-warning" },
  not_published: { label: "Not published", cls: "badge badge-secondary" },
};

/**
 * HR: Organizational Charts list (Employee List table style) + "New chart" dialog. A new chart
 * starts empty; HR adds the people in the editor.
 */
class OrgChartCharts extends Component {
  constructor(props) {
    super(props);
    this.state = { showNew: false, name: "", saving: false, error: null };
  }

  componentDidMount() {
    this.props.fetchManageList();
  }

  open = () => this.setState({ showNew: true, name: "", saving: false, error: null });

  close = () => { if (!this.state.saving) this.setState({ showNew: false }); };

  create = (e) => {
    e.preventDefault();
    const name = this.state.name.trim();
    if (!name) return this.setState({ error: "Please give the chart a name." });
    this.setState({ saving: true, error: null });
    return this.props.createChart({ name })
      .then((draft) => this.props.history.push(global.links.org_chart_manage + draft.chart.id))
      .catch((err) => this.setState({ saving: false, error: err && err.status === 422 ? errorText(err) : null }));
  };

  move = (index, step) => {
    const ids = this.props.charts.map((c) => c.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + step, 0, moved);
    this.props.reorderCharts(ids);
  };

  remove = (chart) => {
    if (window.confirm('Delete the chart "' + chart.name + '"? Staff will no longer see it. This cannot be undone.')) {
      this.props.deleteChart(chart.id);
    }
  };

  render() {
    const { charts, loaded } = this.props;
    const s = this.state;
    const searchTimeout = null;

    return (
      <div className="org-chart">
        <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
          <h1 className="oc-page-title">Organizational Charts</h1>
          <div>
            <Link to={global.links.dashboard || "/app/Dashboard"} className="btn btn-secondary"><i className="fa fa-arrow-left" aria-hidden="true" />Back to dashboard</Link>{" "}
            <button type="button" className="btn btn-primary" onClick={this.open}><i className="fa fa-plus" aria-hidden="true" />New chart</button>
          </div>
        </div>

        {!loaded ? (
          <p className="oc-state" role="status"><i className="fa fa-spinner fa-spin" aria-hidden="true" />Loading charts…</p>
        ) : charts.length === 0 ? (
          <div className="oc-card oc-empty">
            <i className="fa fa-sitemap" aria-hidden="true" />
            <h3>No charts yet</h3>
            <p>Create the first chart, then add the people. Staff see it once you publish.</p>
            <button type="button" className="btn btn-primary" onClick={this.open}><i className="fa fa-plus" aria-hidden="true" />New chart</button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm oc-table mb-1">
              <thead>
                <tr><th>Order</th><th>Chart</th><th>Top of chart</th><th>Boxes</th><th>Status</th><th>Last changed</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {charts.map((c, i) => (
                  <tr key={c.id}>
                    <td className="text-nowrap">
                      <button type="button" className="oc-act" onClick={() => this.move(i, -1)} disabled={i === 0} aria-label={"Move " + c.name + " up"}><i className="fa fa-arrow-up" aria-hidden="true" /></button>
                      <button type="button" className="oc-act" onClick={() => this.move(i, 1)} disabled={i === charts.length - 1} aria-label={"Move " + c.name + " down"}><i className="fa fa-arrow-down" aria-hidden="true" /></button>
                    </td>
                    <td>
                      <b>{c.name}</b>
                      {c.leavers > 0 && <><br /><span className="badge badge-danger font-weight-normal">{c.leavers === 1 ? "1 person left the company" : c.leavers + " people left the company"}</span></>}
                    </td>
                    <td>{c.top || <span className="text-muted">Empty</span>}</td>
                    <td>{c.items}</td>
                    <td><span className={STATUS[c.status].cls}>{STATUS[c.status].label}</span></td>
                    <td>{formatDate(c.updated_at)}</td>
                    <td className="text-nowrap">
                      <Link to={global.links.org_chart_manage + c.id} className="oc-act" aria-label={"Edit " + c.name}><i className="fa fa-pencil" aria-hidden="true" /></Link>
                      <button type="button" className="oc-act red" onClick={() => this.remove(c)} aria-label={"Delete " + c.name}><i className="fa fa-trash" aria-hidden="true" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <span className="text-muted">Total: {charts.length}</span>
          </div>
        )}

        <Modal show={s.showNew} onHide={this.close} centered className="org-chart" aria-labelledby="oc-new-title">
          <form onSubmit={this.create} noValidate>
            <Modal.Header closeButton className="oc-modal-head"><Modal.Title id="oc-new-title">New chart</Modal.Title></Modal.Header>
            <Modal.Body>
              {s.error && <div className="alert alert-danger py-2" role="alert">{s.error}</div>}
              <div className="form-group">
                <label className="oc-label" htmlFor="oc-new-name">Chart name</label>
                <input id="oc-new-name" className="form-control" maxLength={80} value={s.name} onChange={(e) => this.setState({ name: e.target.value })} placeholder="e.g. IT" autoFocus />
              </div>
              <div className="form-group">
                <label className="oc-label" htmlFor="oc-new-top">Top of the chart</label>
                <AsyncSelect inputId="oc-new-top" cacheOptions defaultOptions={false} isClearable value={s.top}
                  loadOptions={(q) => {
                    if (!q || q.trim().length < 2) return Promise.resolve([]);
                    
                    clearTimeout(searchTimeout);
                    
                    return new Promise((resolve) => {
                      searchTimeout = setTimeout(() => {
                        resolve(this.props.searchEmployees(q.trim()));
                      }, 300); // 300ms delay
                    });
                  }}
                  className="async-select"
                  onChange={(top) => this.setState({ top })} placeholder="Type at least 2 letters of a name"
                  noOptionsMessage={({ inputValue }) => (inputValue.trim().length < 2 ? "Type at least 2 letters" : "No active employee found")} />
                <small className="text-muted">Leave empty for a chart with several people at the top, like the Executive Committee.</small>
              </div>
              <div className="form-group mb-1">
                <span className="oc-label">Start with</span>
                <div className="custom-control custom-radio">
                  <input type="radio" id="oc-start-fill" className="custom-control-input" checked={s.start === "fill"} onChange={() => this.setState({ start: "fill" })} />
                  <label className="custom-control-label" htmlFor="oc-start-fill">
                    Fill from BambooHR: everyone who reports to this person, down to{" "}
                    <select className="custom-select custom-select-sm d-inline-block w-auto" value={s.levels} aria-label="Levels to pull in"
                      onChange={(e) => this.setState({ levels: Number(e.target.value), start: "fill" })}>
                      <option value={3}>3 levels</option><option value={2}>2 levels</option><option value={1}>1 level</option>
                    </select>
                  </label>
                </div>
                <div className="custom-control custom-radio mt-1">
                  <input type="radio" id="oc-start-empty" className="custom-control-input" checked={s.start === "empty"} onChange={() => this.setState({ start: "empty" })} />
                  <label className="custom-control-label" htmlFor="oc-start-empty">Only the top person (or nobody); I'll add the rest</label>
                </div>
              </div>
              <p className="text-muted mb-0" style={{ fontSize: 11 }}>The chart starts as a draft. Staff see nothing until you publish it.</p>
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn btn-secondary" onClick={this.close} disabled={s.saving}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={s.saving}>
                {s.saving ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" />Creating…</> : <><i className="fa fa-plus" aria-hidden="true" />Create draft</>}
              </button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  charts: (state.orgChart && state.orgChart.manage) || [],
  loaded: !!(state.orgChart && state.orgChart.manageLoaded),
});

const mapDispatchToProps = (dispatch) => ({
  fetchManageList: () => dispatch(fetchManageList()),
  createChart: (data) => dispatch(createChart(data)),
  deleteChart: (id) => dispatch(deleteChart(id)),
  reorderCharts: (ids) => dispatch(reorderCharts(ids)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrgChartCharts);
